import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { BlogSummary, ServiceSummary } from '@/types'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { BusinessSchema } from '@/components/generals/business-schema'
import { HomeHero } from '@/components/sections/home-hero'
import { FeatureStrip } from '@/components/sections/feature-strip'
import { ServicesGrid } from '@/components/sections/services-grid'
import { Story } from '@/components/sections/story'
import { Testimonials } from '@/components/sections/testimonials'
import { Benefit } from '@/components/sections/benefit'
import { Process } from '@/components/sections/process'
import { Insights } from '@/components/sections/insights'
import { Faq } from '@/components/sections/faq'
import { Cta } from '@/components/sections/cta'
import { getBlogSummaries, getServiceSummaries } from '@/lib/content'

type HomeProps = {
    services: ServiceSummary[]
    posts: BlogSummary[]
}

const Home: FC<HomeProps> = ({ services, posts }): ReactNode => {
    return (
        <Layout>
            <Seo title='Professional Painters in New Bedford, MA' />
            <BusinessSchema />

            <HomeHero />
            <FeatureStrip />

            <ServicesGrid
                services={services}
                heading='Our services'
                subheading='Professional painting solutions for every need, from a single room to a full commercial building.'
                ctaLabel='Explore All Services'
                ctaHref='/services'
            />

            <Story />
            <Testimonials />
            <Benefit variant='home' />
            <Process variant='home' />
            <Insights posts={posts} />
            <Faq />
            <Cta />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<HomeProps> = async () => {
    const [services, posts] = await Promise.all([
        getServiceSummaries(),
        getBlogSummaries(4)
    ])

    return {
        props: { services, posts },
        revalidate: 60
    }
}

export default Home
